import { useCallback, useEffect, useRef, useState } from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { CategoryProduct } from '../Shop/Categories'
import { UpsellProductsList } from './UpsellProductsList'
import { useHorizontalDragScroll, useResize } from 'shop/hooks'
import { StyledHTMLElement } from 'shop/theme/types'
import { disableBlueTapHighlight } from '../common'
import {
  IoChevronBack as LeftChevron,
  IoChevronForward as RightChevron
} from 'react-icons/io5'

type UpsellCarouselProps = {
  upsellProducts: CategoryProduct[]
  onProductsUpdate: (products: CategoryProduct[]) => void
  onAllProductsAdded?: () => void
  shopCartFooterRef: React.RefObject<HTMLDivElement>
}

export const UpsellCarousel = ({
  upsellProducts,
  onProductsUpdate,
  onAllProductsAdded,
  shopCartFooterRef
}: UpsellCarouselProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const containerSize = useResize(scrollContainerRef)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [hideScrollButtons, setHideScrollButtons] = useState(false)

  const { onMouseDown, onMouseMove, onMouseUp } =
    useHorizontalDragScroll(scrollContainerRef)

  const onScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current
      let containerWidth = 0

      if (containerSize?.width && containerSize?.x) {
        containerWidth = containerSize?.width + containerSize?.x * 2
      }
      const isScrollable =
        (scrollContainerRef.current?.scrollWidth || 0) > containerWidth

      setCanScrollLeft(isScrollable && scrollLeft > 0)
      setCanScrollRight(
        isScrollable && Math.round(scrollLeft + clientWidth) < scrollWidth
      )
    }
  }, [containerSize])

  useEffect(() => {
    if (upsellProducts.length) {
      onScroll()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsellProducts])

  /** Hide scroll buttons when carousel approaches sticky footer to prevent overlap */
  useEffect(() => {
    if (!scrollContainerRef?.current) return
    if (!shopCartFooterRef?.current) return

    const shopFooterHeight = shopCartFooterRef.current.offsetHeight

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Hide when carousel crosses into footer zone (below 90% visible in adjusted viewport)
        setHideScrollButtons(entry.intersectionRatio < 0.9)
      },
      {
        threshold: 0.9, // Fire callback at 90% visibility boundary
        rootMargin: `0px 0px -${shopFooterHeight}px 0px` // Shrink viewport by footer height to create "footer zone"
      }
    )

    observer.observe(scrollContainerRef.current)

    return () => {
      observer.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleScrollButtonClick = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const shiftAmount = scrollContainerRef.current.clientWidth * 0.8
      if (direction === 'left') {
        scrollContainerRef.current.scrollBy({
          left: -shiftAmount,
          behavior: 'smooth'
        })
      }
      if (direction === 'right') {
        scrollContainerRef.current.scrollBy({
          left: shiftAmount,
          behavior: 'smooth'
        })
      }
    }
  }

  return (
    <div>
      {!hideScrollButtons && canScrollLeft && (
        <>
          <ScrollIndicatorIcon
            side='left'
            onClick={() => handleScrollButtonClick('left')}
          >
            <LeftChevron />
          </ScrollIndicatorIcon>
          <CircleBackground side='left' />
        </>
      )}
      <CarouselScrollWrapper
        ref={scrollContainerRef}
        onWheel={(e) => e.stopPropagation()}
        onScroll={onScroll}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        <UpsellProductsList
          upsellProducts={upsellProducts}
          onProductsUpdate={onProductsUpdate}
          layout='carousel'
          onAllProductsAdded={onAllProductsAdded}
        />
      </CarouselScrollWrapper>
      {!hideScrollButtons && canScrollRight && (
        <>
          <ScrollIndicatorIcon
            side='right'
            onClick={() => handleScrollButtonClick('right')}
          >
            <RightChevron />
          </ScrollIndicatorIcon>
          <CircleBackground side='right' />
        </>
      )}
    </div>
  )
}

const CarouselScrollWrapper = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    cursor: 'grab',
    scrollSnapType: 'x mandatory',

    '&::-webkit-scrollbar': {
      display: 'none'
    },

    '&:active': {
      cursor: 'grabbing'
    }
  })
)

const ScrollIndicatorIcon = styled.div<
  StyledHTMLElement & { side: 'left' | 'right' }
>(({ theme, side }) => ({
  display: 'none',
  alignContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  top: '58%',
  transform: 'translateY(-50%)',
  padding: '8px',
  borderRadius: '32px',
  border: '1px solid #fff',
  backgroundColor: theme.colors['primary'],
  zIndex: 2,
  cursor: 'pointer',

  ...(side === 'left' && {
    left: '-6px'
  }),
  ...(side === 'right' && {
    right: '-6px'
  }),

  '& svg': {
    height: '20px',
    width: '20px',
    color: '#fff'
  },

  ':hover': {
    opacity: 0.8
  },

  '&:active': {
    opacity: 0.6
  },

  [theme.mediaQueries.viewport7]: {
    display: 'flex'
  },

  ...disableBlueTapHighlight()
}))

const CircleBackground = styled.div<
  StyledHTMLElement & { side: 'left' | 'right' }
>(({ theme, side }) => ({
  display: 'none',
  position: 'absolute',
  height: '34px',
  width: '34px',
  borderRadius: '36px',
  zIndex: 1,
  top: '45%',
  transform: 'translateY(-50%)',

  ...(side === 'left' && {
    left: 0,
    marginLeft: '-16px'
  }),
  ...(side === 'right' && {
    right: 0,
    marginRight: '-16px'
  }),

  [theme.mediaQueries.viewport7]: {
    display: 'block'
  }
}))
